import { ref, reactive } from 'vue';
import { Functions } from 'appwrite';
import { client, appwriteConfig } from './appwrite';

let instance = null;

export function useUsers() {
    if (instance) return instance;

    const users = reactive({});
    const isLoading = ref(false);
    const error = ref(null);
    const functions = new Functions(client);

    const fetchUserLabels = async () => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await functions.createExecution(appwriteConfig.functions.getUserLabels);
            const result = JSON.parse(response.responseBody);
            Object.assign(users, result);
        } catch (err) {
            console.error('Error fetching user labels:', err);
            error.value = err;
        } finally {
            isLoading.value = false;
        }
    };

    const setUserLabel = async (userId, label) => {
        isLoading.value = true;
        error.value = null;
        try {
            const user = getUsers(userId);
            user.label = label;
            if (!user) {
                throw new Error('User not found');
            }
            const response = await functions.createExecution(appwriteConfig.functions.setUserLabel, JSON.stringify({ userId, label }));
            const result = JSON.parse(response.response);
            if (result.success) {
                user.label = label;
            } else {
                throw new Error(result.message || 'Failed to set user label');
            }
        } catch (err) {
            console.error('Error setting user label:', err);
            error.value = err;
        } finally {
            isLoading.value = false;
        }
    };

    const deleteUser = async (userId) => {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await functions.createExecution(appwriteConfig.functions.deleteUser, JSON.stringify({ userId }));
            const result = JSON.parse(response.response);
            if (result.success) {
                delete users[userId];
            } else {
                throw new Error(result.message || 'Failed to delete user');
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            error.value = err;
        } finally {
            isLoading.value = false;
        }
    };

    const getUsers = (userId) => {
        return Object.values(users).find(user => user.id === userId);
    };

    fetchUserLabels();

    instance = {
        users,
        isLoading,
        error,
        fetchUserLabels,
        setUserLabel,
        deleteUser,
        getUsers
    };

    return instance;
}